"use client";

import { useEffect, useMemo, useState } from "react";
import { LoadingOnboardingModalContent } from "../LoadingStep/LoadingOnboardingModalContent";
import { TrackersSetupOnboardingModalContent } from "../ResultStep/TrackersSetupOnboardingModalContent";
import { mapCollectedFieldsToTrackersSetupPayload } from "./mapCollectedFieldsToTrackersSetupPayload";
import {
  parseAggregatedOnboardingInput,
  resolveInitialAggregatedStep,
} from "./parseAggregatedOnboardingInput";
import { AGGREGATED_LOADING_LAYOUT } from "./aggregatedLoadingLayout.contract";
import type {
  AggregatedOnboardingFlowResult,
  AggregatedOnboardingFlowStep,
} from "./types";

type AggregatedOnboardingFlowContentProps = {
  /** 来自 `openAggregatedOnboardingModal` 的 SaveGoal 字段袋 */
  collectedFields?: Record<string, unknown>;
  /** ⑥ Loading 背景；未传则用聚合流默认（见下方常量） */
  loadingBackgroundSrc?: string;
  /** 任意终态关闭时回调（loading_exit / confirmed / saved） */
  onFinish: (result: AggregatedOnboardingFlowResult) => void;
};

/** 聚合流 ⑥ Loading 默认背景（trial）；`openAggregatedOnboardingModal({ loadingBackgroundSrc })` 可覆盖 */
const AGGREGATED_LOADING_BACKGROUND_SRC = "/assets/images/onboard-bg.webp";

/** ⑥ Loading 最短展示时长，结束后直接进入 ⑧（不请求 generate、不展示诊断页） */
const AGGREGATED_LOADING_DURATION_MS = 2000;

/**
 * 聚合弹窗**内部状态机**：在同一 Modal children 下切换 ⑥ / ⑧ UI。
 *
 * 复用各 Step 的 Presentational 组件，不嵌套调用 `openLoadingOnboardingModal` 等入口。
 */
export const AggregatedOnboardingFlowContent: React.FC<
  AggregatedOnboardingFlowContentProps
> = ({ collectedFields, loadingBackgroundSrc, onFinish }) => {
  const parsed = useMemo(
    () => parseAggregatedOnboardingInput(collectedFields),
    [collectedFields],
  );

  /** ⑧ 展示用 payload，在首屏为 trackers 时也会用到 */
  const trackersPayload = useMemo(
    () => mapCollectedFieldsToTrackersSetupPayload(parsed, collectedFields),
    [collectedFields, parsed],
  );

  const [step, setStep] = useState<AggregatedOnboardingFlowStep>(() => {
    const initial = resolveInitialAggregatedStep(parsed);
    return initial === "loading" ? "loading" : "trackers";
  });

  /** ⑥ 固定展示时长后进入 ⑧，不调用 onboarding/generate */
  useEffect(() => {
    if (step !== "loading") {
      return;
    }

    const timerId = window.setTimeout(() => {
      setStep("trackers");
    }, AGGREGATED_LOADING_DURATION_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [step]);

  /** ⑥ — 复用同事 Loading UI；退出 → `loading_exit` */
  if (step === "loading") {
    return (
      <div
        className="h-full w-full overflow-hidden md:h-[674px]"
        data-testid="aggregated-onboarding-loading-shell"
      >
        <LoadingOnboardingModalContent
          backgroundSrc={
            loadingBackgroundSrc ?? AGGREGATED_LOADING_BACKGROUND_SRC
          }
          rippleCentered={AGGREGATED_LOADING_LAYOUT.rippleCentered}
          ringPeakOpacity={AGGREGATED_LOADING_LAYOUT.ringPeakOpacity}
          onClose={() => {
            // H5 返回 / PC 关闭均视为 loading_exit
            onFinish({ action: "loading_exit" });
          }}
        />
      </div>
    );
  }

  /** ⑧ 底部 CTA：有 save 回调则 `saved`，否则 `confirmed` */
  const handleTrackersCta = () => {
    if (parsed.handleSubmitGoal) {
      parsed.handleSubmitGoal();
      onFinish({ action: "saved" });
      return;
    }
    onFinish({ action: "confirmed" });
  };

  return (
    <TrackersSetupOnboardingModalContent
      layout="aggregated"
      payload={trackersPayload}
      onClose={() => {
        void handleTrackersCta();
      }}
    />
  );
};
